/* FOM · investigacao — a camada PRIVATE onde uma lacuna vira conhecimento.

   O ciclo, na ordem em que o produto o pensa:

     ND -> pergunta -> evidencia necessaria -> evidencia encontrada
        -> resposta -> classificacao deliberada (COM TETO) -> conhecimento

   Duas coisas que esta tela NAO faz, de proposito:

   1. Nao calcula o teto epistemologico. As classificacoes oferecidas vem de
      GET .../allowed, que consulta a mesma funcao usada pelo trigger C-07.
      Se esta tela calculasse por conta propria, existiriam duas verdades — e
      a divergencia apareceria como erro incompreensivel no encerramento.

   2. Nao decide quem pode ler. `viewer` recebe 403 do servidor; a tela apenas
      explica o 403 em vez de exibir um erro cru. A autoridade continua no
      backend. */
import type { Api } from '../core/api.ts';
import { ApiError } from '../core/api.ts';
import type {
  FomNode, Investigation, InvestigationResumo, StatusClass, EvidenceType, ClosingEffect,
} from '../core/types.ts';
import { ROTULO_ST, ROTULO_INV, ROTULO_EFEITO, COR_ST } from '../core/types.ts';
import { el, botao, campo, entrada, area, selecao, limpar, vazio, dataCurta } from './dom.ts';

const TIPOS_EVIDENCIA: EvidenceType[] = [
  'Tela do sistema', 'Documento', 'Relato do operador', 'Outro',
];

export type InvestigacaoOpcoes = {
  api: Api;
  org: string;
  mapa: string;
  /* chamado quando o encerramento altera o no, para o mapa se atualizar */
  aoAlterarNo: () => Promise<void>;
  aoErro: (erro: unknown) => void;
};

export function montarInvestigacao(host: HTMLElement, o: InvestigacaoOpcoes) {
  let lista: InvestigationResumo[] = [];
  let aberta: Investigation | null = null;
  let permitidas: StatusClass[] = [];
  let noAtual: FomNode | null = null;
  let carregando = false;

  async function recarregarLista(): Promise<void> {
    lista = await o.api.investigacoes(o.org, o.mapa);
  }

  async function abrir(code: string): Promise<void> {
    aberta = await o.api.investigacao(o.org, o.mapa, code);
    /* o teto so importa quando ainda ha o que classificar */
    permitidas = aberta.status === 'encerrada'
      ? []
      : await o.api.classificacoesPermitidas(o.org, o.mapa, code);
  }

  async function comErro(fn: () => Promise<void>): Promise<void> {
    if (carregando) return;
    carregando = true;
    try {
      await fn();
      render();
    } catch (err) {
      o.aoErro(err);
      render();
    } finally {
      carregando = false;
    }
  }

  /* ------------------------------------------------------------ listagem -- */

  function renderLista(): void {
    const cab = el('div', 'gaveta-cabecalho');
    cab.appendChild(el('h3', 'gaveta-titulo', 'Investigacao'));
    cab.appendChild(el('span', 'selo selo-privado', 'PRIVADO'));
    host.appendChild(cab);

    host.appendChild(el('p', 'gaveta-nota',
      'Uma pergunta so vira conhecimento depois de ter evidencia. A classificacao '
      + 'possivel e limitada pela evidencia registrada — o limite e do banco, nao desta tela.'));

    if (noAtual) {
      const nova = botao(
        '+ Nova pergunta sobre "' + noAtual.name + '"',
        () => { renderFormularioNova(noAtual!, null); },
        'botao botao-principal',
      );
      host.appendChild(nova);
    }

    const doNo = noAtual ? lista.filter((i) => i.node_external_id === noAtual!.external_id) : [];
    const outras = noAtual ? lista.filter((i) => i.node_external_id !== noAtual!.external_id) : lista;

    if (!lista.length) {
      host.appendChild(vazio(
        'Nenhuma investigacao aberta neste mapa.',
        'Toda lacuna do mapa pode virar uma pergunta. Comece por um no com lacunas.',
      ));
      return;
    }

    if (noAtual && doNo.length) {
      host.appendChild(el('h4', 'gaveta-secao', 'Deste no · ' + String(doNo.length)));
      host.appendChild(tabela(doNo));
    }
    if (outras.length) {
      host.appendChild(el('h4', 'gaveta-secao',
        (noAtual ? 'Outros nos · ' : 'Todas · ') + String(outras.length)));
      host.appendChild(tabela(outras));
    }
  }

  function tabela(itens: InvestigationResumo[]): HTMLElement {
    const ul = el('ul', 'lista-inv');
    for (const i of itens) {
      const li = el('li', 'inv-item inv-' + i.status);
      const b = botao(i.code, () => { void comErro(() => abrir(i.code)); }, 'inv-codigo');
      li.appendChild(b);
      li.appendChild(el('span', 'inv-pergunta', i.question));
      li.appendChild(el('span', 'inv-status', ROTULO_INV[i.status]));
      ul.appendChild(li);
    }
    return ul;
  }

  /* ------------------------------------------------------- nova pergunta -- */

  function renderFormularioNova(no: FomNode, lacuna: string | null): void {
    limpar(host);
    host.appendChild(el('h3', 'gaveta-titulo', 'Nova pergunta'));
    host.appendChild(el('p', 'gaveta-nota', 'Sobre: ' + no.name));

    const form = el('form', 'formulario');
    form.noValidate = true;

    const pergunta = area(lacuna ?? '', 3, 'O que exatamente precisa ser descoberto?');
    const precisa = area('', 2, 'Que evidencia responderia isso?');
    form.appendChild(campo('Pergunta', pergunta, 'minimo 3 caracteres'));
    form.appendChild(campo('Evidencia necessaria', precisa, 'opcional, mas e o que guia a busca'));

    if (lacuna) {
      const origem = el('div', 'origem-lacuna');
      origem.appendChild(el('span', 'campo-rotulo', 'Origem'));
      origem.appendChild(el('span', 'campo-valor', 'lacuna do mapa: ' + lacuna));
      form.appendChild(origem);
    }

    const erro = el('p', 'form-erro');
    erro.hidden = true;
    form.appendChild(erro);

    const acoes = el('div', 'formulario-acoes');
    acoes.appendChild(botao('Cancelar', () => { render(); }));
    const enviar = el('button', 'botao botao-principal', 'Criar pergunta');
    enviar.type = 'submit';
    acoes.appendChild(enviar);
    form.appendChild(acoes);

    form.addEventListener('submit', (ev) => {
      ev.preventDefault();
      if (pergunta.value.trim().length < 3) {
        erro.textContent = 'A pergunta precisa de pelo menos 3 caracteres.';
        erro.hidden = false;
        return;
      }
      void comErro(async () => {
        const criada = await o.api.criarInvestigacao(o.org, o.mapa, {
          node_external_id: no.external_id,
          question: pergunta.value.trim(),
          ...(precisa.value.trim() ? { needed: precisa.value.trim() } : {}),
          ...(lacuna ? { origin_gap: lacuna } : {}),
        });
        await recarregarLista();
        aberta = criada;
        permitidas = await o.api.classificacoesPermitidas(o.org, o.mapa, criada.code);
      });
    });

    host.appendChild(form);
  }

  /* --------------------------------------------------------- detalhe ----- */

  function renderDetalhe(inv: Investigation): void {
    const cab = el('div', 'gaveta-cabecalho');
    cab.appendChild(botao('‹ voltar', () => { aberta = null; render(); }, 'botao-link'));
    cab.appendChild(el('h3', 'gaveta-titulo', inv.code));
    cab.appendChild(el('span', 'inv-status inv-status-' + inv.status, ROTULO_INV[inv.status]));
    host.appendChild(cab);

    host.appendChild(el('p', 'inv-pergunta-grande', inv.question));

    /* onde esta a pergunta na cadeia que transforma duvida em conhecimento */
    const cadeia = el('div', 'cadeia');
    cadeia.setAttribute('role', 'img');
    cadeia.setAttribute('aria-label', 'Cadeia: evidencia sustenta resposta, que sustenta conhecimento');
    const etapa = (rotulo: string, valor: string, ok: boolean): HTMLElement => {
      const d = el('div', 'cadeia-etapa' + (ok ? ' cadeia-etapa-ok' : ''));
      d.appendChild(el('span', 'cadeia-rotulo', rotulo));
      d.appendChild(el('span', 'cadeia-valor', valor));
      return d;
    };
    const temConhecimento = inv.status === 'encerrada'
      && inv.closing?.effect !== undefined && inv.closing.effect !== 'log';
    cadeia.appendChild(etapa('Evidencia',
      inv.evidence.length ? String(inv.evidence.length) + ' registrada(s)' : 'pendente',
      inv.evidence.length > 0));
    cadeia.appendChild(el('span', 'cadeia-seta', '→'));
    cadeia.appendChild(etapa('Resposta',
      inv.answer_text ? 'registrada' : 'pendente', !!inv.answer_text));
    cadeia.appendChild(el('span', 'cadeia-seta', '→'));
    cadeia.appendChild(etapa('Conhecimento',
      temConhecimento ? 'no mapa'
        : inv.status === 'encerrada' ? 'registro (log)' : 'pendente',
      Boolean(temConhecimento)));
    host.appendChild(cadeia);
    if (inv.needed) {
      host.appendChild(linha('Evidencia necessaria', inv.needed));
    }
    if (inv.origin_gap) {
      host.appendChild(linha('Origem', 'lacuna do mapa: ' + inv.origin_gap));
    }
    host.appendChild(linha('No', inv.node_external_id));

    /* --- evidencia --- */
    host.appendChild(el('h4', 'gaveta-secao', 'Evidencia · ' + String(inv.evidence.length)));
    if (!inv.evidence.length) {
      host.appendChild(vazio(
        'Nenhuma evidencia ainda.',
        'Sem evidencia nao se responde — e sem responder nao se classifica.',
      ));
    } else {
      const ul = el('ul', 'lista-evidencia');
      for (const e of inv.evidence) {
        const li = el('li', 'evidencia-item');
        li.appendChild(el('span', 'evidencia-tipo', e.type));
        if (e.partial) li.appendChild(el('span', 'selo selo-parcial', 'parcial'));
        if (e.ref) li.appendChild(el('span', 'evidencia-ref', e.ref));
        if (e.description) li.appendChild(el('span', 'evidencia-desc', e.description));
        li.appendChild(el('span', 'evidencia-data', dataCurta(e.evidence_date ?? e.created_at)));
        ul.appendChild(li);
      }
      host.appendChild(ul);
    }

    if (inv.status !== 'encerrada') host.appendChild(formEvidencia(inv));

    /* --- teto --- */
    if (inv.status !== 'encerrada') host.appendChild(painelTeto());

    /* --- resposta --- */
    if (inv.answer_text) {
      host.appendChild(el('h4', 'gaveta-secao', 'Resposta'));
      host.appendChild(el('p', 'inv-resposta', inv.answer_text));
    }
    if (inv.status === 'aberta') host.appendChild(formResposta(inv));

    /* --- encerramento --- */
    if (inv.status === 'respondida') host.appendChild(formEncerrar(inv));
    if (inv.status === 'encerrada' && inv.closing) host.appendChild(blocoEncerrada(inv));
  }

  function linha(rotulo: string, valor: string): HTMLElement {
    const d = el('div', 'inv-linha');
    d.appendChild(el('span', 'campo-rotulo', rotulo));
    d.appendChild(el('span', 'campo-valor', valor));
    return d;
  }

  /* O teto, dito antes de o usuario tentar. Explicar a regra e melhor do que
     deixar o banco recusar depois. */
  function painelTeto(): HTMLElement {
    const d = el('div', 'teto');
    d.appendChild(el('span', 'campo-rotulo', 'Classificacao possivel com a evidencia atual'));
    const faixa = el('div', 'teto-faixa');
    if (!permitidas.length) {
      faixa.appendChild(el('span', 'teto-vazio', 'nenhuma — registre evidencia primeiro'));
    } else {
      for (const st of permitidas) {
        const p = el('span', 'teto-item', st);
        p.style.borderColor = COR_ST[st];
        p.title = ROTULO_ST[st];
        faixa.appendChild(p);
      }
    }
    d.appendChild(faixa);
    return d;
  }

  function formEvidencia(inv: Investigation): HTMLElement {
    const form = el('form', 'formulario formulario-inline');
    form.noValidate = true;

    const tipo = selecao(TIPOS_EVIDENCIA.map((t) => ({ valor: t, rotulo: t })), 'Documento');
    const ref = entrada('', 'onde isso pode ser visto');
    const desc = area('', 2, 'o que a evidencia mostra');
    const parcial = el('input');
    parcial.type = 'checkbox';

    form.appendChild(campo('Tipo', tipo));
    form.appendChild(campo('Referencia', ref));
    form.appendChild(campo('Descricao', desc));

    const linhaParcial = el('label', 'campo campo-checkbox');
    linhaParcial.appendChild(parcial);
    linhaParcial.appendChild(el('span', 'campo-rotulo', 'evidencia parcial'));
    linhaParcial.appendChild(el('span', 'campo-dica',
      'parcial rebaixa o teto: tela completa permite E1, tela parcial nao'));
    form.appendChild(linhaParcial);

    const acoes = el('div', 'formulario-acoes');
    const enviar = el('button', 'botao', 'Registrar evidencia');
    enviar.type = 'submit';
    acoes.appendChild(enviar);
    form.appendChild(acoes);

    form.addEventListener('submit', (ev) => {
      ev.preventDefault();
      void comErro(async () => {
        await o.api.registrarEvidencia(o.org, o.mapa, inv.code, {
          type: tipo.value as EvidenceType,
          ...(ref.value.trim() ? { ref: ref.value.trim() } : {}),
          ...(desc.value.trim() ? { description: desc.value.trim() } : {}),
          partial: parcial.checked,
        });
        await abrir(inv.code);
      });
    });
    return form;
  }

  function formResposta(inv: Investigation): HTMLElement {
    const semEvidencia = inv.evidence.length === 0;
    const form = el('form', 'formulario');
    form.noValidate = true;
    form.appendChild(el('h4', 'gaveta-secao', 'Responder'));

    const txt = area('', 3, 'o que a evidencia permite afirmar');
    txt.disabled = semEvidencia;
    form.appendChild(campo('Resposta', txt,
      semEvidencia ? 'bloqueado: nao se responde antes de ter evidencia' : undefined));

    const acoes = el('div', 'formulario-acoes');
    const enviar = el('button', 'botao botao-principal', 'Registrar resposta');
    enviar.type = 'submit';
    enviar.disabled = semEvidencia;
    acoes.appendChild(enviar);
    form.appendChild(acoes);

    form.addEventListener('submit', (ev) => {
      ev.preventDefault();
      if (!txt.value.trim()) return;
      void comErro(async () => {
        await o.api.responderInvestigacao(o.org, o.mapa, inv.code, txt.value.trim());
        await abrir(inv.code);
        await recarregarLista();
      });
    });
    return form;
  }

  function formEncerrar(inv: Investigation): HTMLElement {
    const form = el('form', 'formulario');
    form.noValidate = true;
    form.appendChild(el('h4', 'gaveta-secao', 'Encerrar'));

    const efeito = selecao(
      (['know', 'reclass', 'log'] as ClosingEffect[])
        .map((e) => ({ valor: e, rotulo: ROTULO_EFEITO[e] })),
      'know',
    );
    /* SO o que o teto permite. A lista vem do servidor; se estiver vazia, nao
       ha classificacao possivel e o encerramento com efeito fica bloqueado. */
    const classe = selecao(
      permitidas.length
        ? permitidas.map((s) => ({ valor: s, rotulo: ROTULO_ST[s] }))
        : [{ valor: '', rotulo: 'nenhuma permitida pela evidencia', desabilitado: true }],
    );
    const texto = area('', 3, 'o conhecimento que fica registrado no no');

    form.appendChild(campo('Efeito', efeito));
    const linhaClasse = campo('Classificacao', classe,
      'limitada pela evidencia registrada — regra do banco (C-07)');
    form.appendChild(linhaClasse);
    form.appendChild(campo('Texto', texto));

    function ajustar(): void {
      const soLog = efeito.value === 'log';
      classe.disabled = soLog || !permitidas.length;
      linhaClasse.hidden = soLog;
    }
    efeito.addEventListener('change', ajustar);
    ajustar();

    const erro = el('p', 'form-erro');
    erro.hidden = true;
    form.appendChild(erro);

    const acoes = el('div', 'formulario-acoes');
    const enviar = el('button', 'botao botao-principal', 'Encerrar investigacao');
    enviar.type = 'submit';
    acoes.appendChild(enviar);
    form.appendChild(acoes);

    form.addEventListener('submit', (ev) => {
      ev.preventDefault();
      const ef = efeito.value as ClosingEffect;
      if (ef !== 'log' && !classe.value) {
        erro.textContent = 'Encerrar com efeito de conhecimento exige uma classificacao — '
          + 'e a evidencia atual nao permite nenhuma.';
        erro.hidden = false;
        return;
      }
      void comErro(async () => {
        await o.api.encerrarInvestigacao(o.org, o.mapa, inv.code, {
          effect: ef,
          ...(ef === 'log' ? {} : { to: classe.value as StatusClass }),
          ...(texto.value.trim() ? { text: texto.value.trim() } : {}),
        });
        await abrir(inv.code);
        await recarregarLista();
        /* o encerramento com efeito muda o no: o mapa precisa saber */
        if (ef !== 'log') await o.aoAlterarNo();
      });
    });
    return form;
  }

  function blocoEncerrada(inv: Investigation): HTMLElement {
    const d = el('div', 'encerramento');
    d.appendChild(el('h4', 'gaveta-secao', 'Encerramento'));
    const c = inv.closing!;
    if (c.effect === 'log') {
      d.appendChild(linha('Efeito', 'apenas registro — o no continua ' + c.kept));
      if (c.reason) d.appendChild(linha('Motivo', c.reason));
    } else {
      d.appendChild(linha('Efeito', ROTULO_EFEITO[c.effect]));
      d.appendChild(linha('Classificacao', c.from + ' → ' + c.to));
      if (c.evidenceRefs) d.appendChild(linha('Evidencia', c.evidenceRefs));
    }
    return d;
  }

  /* ------------------------------------------------------------- render -- */

  function render(): void {
    limpar(host);
    if (aberta) renderDetalhe(aberta);
    else renderLista();
  }

  function semAcesso(): void {
    limpar(host);
    host.appendChild(el('h3', 'gaveta-titulo', 'Investigacao'));
    host.appendChild(vazio(
      'Camada privada.',
      'Seu papel nesta organizacao nao le investigacoes. Isso e decidido pelo '
      + 'servidor, nao por esta tela.',
    ));
  }

  return {
    /* Chamado pela gaveta ao abrir a aba. Recarrega do servidor: a lista de
       investigacoes nao e cache de tela. */
    async mostrar(no: FomNode | null): Promise<void> {
      noAtual = no;
      try {
        await recarregarLista();
        if (aberta) await abrir(aberta.code);
        render();
      } catch (err) {
        if (err instanceof ApiError && err.status === 403) { semAcesso(); return; }
        o.aoErro(err);
      }
    },
    /* Entrada direta a partir de uma lacuna do mapa: o produto inteiro existe
       para esta transicao. */
    async investigarLacuna(no: FomNode, lacuna: string): Promise<void> {
      noAtual = no;
      aberta = null;
      try {
        await recarregarLista();
        renderFormularioNova(no, lacuna);
      } catch (err) {
        if (err instanceof ApiError && err.status === 403) { semAcesso(); return; }
        o.aoErro(err);
      }
    },
  };
}
